import { jsx as _jsx } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
const NavbarChromeContext = createContext({
    tint: null,
    setTint: () => { },
    blurDepth: 0,
    setBlurDepth: () => { }
});
export const NavbarChromeProvider = ({ children }) => {
    const [tint, setTint] = useState(null);
    const [blurDepth, setBlurDepth] = useState(0);
    const value = useMemo(() => ({ tint, setTint, blurDepth, setBlurDepth }), [tint, blurDepth]);
    return _jsx(NavbarChromeContext.Provider, { value: value, children: children });
};
// eslint-disable-next-line react-refresh/only-export-components
export function useExtensionTint() {
    return useContext(NavbarChromeContext).tint;
}
// eslint-disable-next-line react-refresh/only-export-components
export function useSetExtensionTint() {
    return useContext(NavbarChromeContext).setTint;
}
/** The navbar reads how far pages want the blur fan extended. */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavbarBlurExtent() {
    return useContext(NavbarChromeContext).blurDepth;
}
/**
 * Extends the navbar's blur fan by the given depth (px) while the calling component is mounted —
 * for page sections pinned under the navbar (sticky headers, tab rows) that float on the fan.
 * Only one mounted component may extend the fan at a time. There is no ownership stack: the last
 * writer sets the depth, and whichever writer unmounts first resets it to 0, so a second concurrent
 * writer would lose the fan while still mounted.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useExtendNavbarBlur(depth) {
    const { setBlurDepth } = useContext(NavbarChromeContext);
    useEffect(() => {
        setBlurDepth(depth);
        return () => setBlurDepth(0);
    }, [depth, setBlurDepth]);
}
//# sourceMappingURL=navbar-chrome-context.js.map